// Render plugin for `analysis_flagged` SSE events.
//
// The agent calls flag_advanced_analysis when a turn runs a method whose
// results have not been checked by a person (regression, key driver, TURF,
// segmentation, factor, correspondence, conjoint). The server composes the
// notice sentence once (it names the customer's stats reviewer) and the tool
// echoes it back in this event, so the card only draws what it is given: the
// same words appear live, after a reload and on a colleague's read-only view.
import React from 'react'
import type { RenderPlugin, PluginAgentSSEEvent } from '../agent'
import { CARD_SX } from '../agent'
import { Box, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import type { AnalysisFlag } from '../types/gotypes'

type NoticePluginState = Map<string, AnalysisFlag>

const capitalise = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

const AnalysisNotice: React.FC<{ flag: AnalysisFlag }> = ({ flag }) => (
  <Box
    data-testid="analysis-notice"
    data-severity="warning"
    role="note"
    sx={(theme) => ({
      ...CARD_SX,
      my: 1,
      p: 1.5,
      display: 'flex',
      gap: 1.25,
      alignItems: 'flex-start',
      backgroundColor: alpha(theme.palette.warning.main, 0.08),
      borderColor: alpha(theme.palette.warning.main, 0.4),
    })}
  >
    <WarningAmberOutlinedIcon color="warning" fontSize="small" sx={{ mt: '2px' }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" component="h4">
        {capitalise(flag.method_label)}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {flag.line}
      </Typography>
      {flag.summary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {flag.summary}
        </Typography>
      )}
    </Box>
  </Box>
)

export const analysisNoticePlugin: RenderPlugin<NoticePluginState> = {
  eventTypes: ['analysis_flagged'],

  init(): NoticePluginState {
    return new Map()
  },

  reduce(state: NoticePluginState, event: PluginAgentSSEEvent): NoticePluginState {
    const d = event.data
    // Key on the event id; fall back to toolCallId plus fold position so two
    // flags from one tool call both show, and replay gives the same keys.
    const toolCallId = d.toolCallId as string | undefined
    const id = (d.id as string | undefined) || (toolCallId ? `${toolCallId}:${state.size}` : undefined)
    const line = d.line as string | undefined
    if (!id || !line) return state
    const next = new Map(state)
    next.set(id, {
      id,
      method: (d.method as string) || '',
      method_label: (d.method_label as string) || 'advanced analysis',
      summary: (d.summary as string) || '',
      reviewer: (d.reviewer as AnalysisFlag['reviewer']) || { name: '', contact: '', team: '' },
      line,
      created_at: (d.created_at as number) || 0,
    })
    return next
  },

  render({ state }: { state: NoticePluginState; toolCallId: string; sessionId: string }) {
    if (state.size === 0) return null
    return (
      <>
        {Array.from(state.values()).map((flag) => (
          <AnalysisNotice key={flag.id} flag={flag} />
        ))}
      </>
    )
  },
}

export default analysisNoticePlugin
